import type {
  issueFields,
  ZodIssueMetadata,
} from '@safc/configuration';
import {
  CertificationScheme,
  CertificationSchemeOther,
  CertificationSchemeToLabel,
  CertificationType,
} from '@safc/configuration';
import { FormDatePicker } from 'components/ui/controlled-form/FormDatePicker/FormDatePicker.js';
import { FormSelect } from 'components/ui/controlled-form/FormSelect/FormSelect.js';
import { RadioGroupForm } from 'components/ui/controlled-form/RadioGroupForm/RadioGroupForm.js';
import { FormInput } from 'components/ui/form/FormInput/FormInput.js';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import type {
  Control,
  UseFormSetValue,
} from 'react-hook-form';

import { IssueFormRow } from '../components/IssueFormRow.js';
import { IssueFormYesNo } from '../components/IssueFormYesNo.js';

type Props = {
  control: Control<any>;
  scheme: CertificationScheme | undefined;
  additionalScheme: CertificationScheme | typeof CertificationSchemeOther | undefined;
  isLowILUCRiskConfirmed: boolean | undefined;
  isLowDisplacementRiskConfirmed: boolean | undefined;
  wasCertifiedAsProductionResidue: boolean | undefined;
  isLanduseBasedCropBased: boolean | undefined;
  shouldValidate: boolean;
  setValue: UseFormSetValue<ZodIssueMetadata>;
};

export const IssueFormCertification = ({
  control,
  additionalScheme,
  isLowDisplacementRiskConfirmed,
  shouldValidate,
  scheme,
  isLowILUCRiskConfirmed,
  wasCertifiedAsProductionResidue,
  isLanduseBasedCropBased,
  setValue,
}: Props) => {
  useEffect(() => {
    // functions for cleaning dependant values
    if (scheme !== CertificationScheme.ISCCPLUS) {
      setValue('certification.hasIRS7210ForIsccPlus', undefined);
    }

    if (scheme !== CertificationScheme.ISCCEU && scheme !== CertificationScheme.RSBEURED) {
      setValue('certification.hasSeparateRFNBORCF', undefined);
    }
    if (additionalScheme !== CertificationScheme.ISCCPLUS) {
      setValue('certification.additionalHasIRS7210ForIsccPlus', undefined);
    }

    if (additionalScheme !== CertificationScheme.ISCCEU && additionalScheme !== CertificationScheme.RSBEURED) {
      setValue('certification.additionalHasSeparateRFNBORCF', undefined);
    }
  }, [scheme, additionalScheme]);

  return (
    <>
      <IssueFormRow control={control} field="certification.scheme">
        {props => (
          <FormSelect
            control={props.control}
            // We mute errors, because it tries to immediately validate the certification settings
            // upon selecting the scheme, which is too eager
            errorExists={false}
            errorText=""
            field={{
              ...props.field,
              options: Object.values(CertificationScheme).map(value => ({ value, label: CertificationSchemeToLabel[value] })),
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={control} field="certification.type">
        {props => (
          <RadioGroupForm
            control={props.control}
            field={{
              ...props.field,
              options: [
                { label: 'Default', value: CertificationType.Default },
                { label: 'Actual', value: CertificationType.Actual },
              ],
            }}
          />
        )}
      </IssueFormRow>
      {scheme === CertificationScheme.ISCCPLUS && (
        <IssueFormRow control={control} field="certification.hasIRS7210ForIsccPlus">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      {(scheme === CertificationScheme.RSBEURED || scheme === CertificationScheme.ISCCEU) && (
        <IssueFormRow control={control} field="certification.hasSeparateRFNBORCF">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      <CertificationDetails
        shouldValidate={shouldValidate}
        control={control}
        nameField="certification.certificationBody"
        emailField="certification.certificationBodyEmail"
        expiryDateField="certification.expiryDate"
        numberField="certification.certificateNumber"
      />
      <IssueFormRow control={control} field="certification.traderCertificationNumber">
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={control} field="certification.additionalScheme">
        {props => (
          <FormSelect
            control={props.control}
            // We mute errors, because it tries to immediately validate the certification settings
            // upon selecting the scheme, which is too eager
            errorExists={false}
            errorText=""
            clearable
            field={{
              ...props.field,
              options: Object.values(CertificationScheme)
                .map(value => ({ value, label: CertificationSchemeToLabel[value] })),
            }}
          />
        )}
      </IssueFormRow>
      {additionalScheme === CertificationScheme.ISCCPLUS && (
        <IssueFormRow control={control} field="certification.additionalHasIRS7210ForIsccPlus">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      {(additionalScheme === CertificationScheme.RSBEURED || additionalScheme === CertificationScheme.ISCCEU) && (
        <IssueFormRow control={control} field="certification.additionalHasSeparateRFNBORCF">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      {additionalScheme === CertificationSchemeOther && (
        <IssueFormRow control={control} field="certification.additionalSchemeName">
          {props => <FormInput {...props} />}
        </IssueFormRow>
      )}
      {additionalScheme !== undefined && (
        <CertificationDetails
          shouldValidate={shouldValidate}
          control={control}
          nameField="certification.additionalCertificationBody"
          emailField="certification.additionalCertificationBodyEmail"
          expiryDateField="certification.additionalExpiryDate"
          numberField="certification.additionalCertificateNumber"
        />
      )}
      {(isLanduseBasedCropBased === true) && (
        <IssueFormRow control={control} field="certification.isLowILUCRiskConfirmed">
          {props => (
            <IssueFormYesNo
              control={props.control}
              field={{
                ...props.field,
                // if feedstock.wasCertifiedAsProductionResidue=false, then this field is forced to be set to "false", and cannot be changed
                // casting to trick TS, otherwise it knows that true !== false
                disabled: (isLanduseBasedCropBased as boolean) === false,
              }}
            />
          )}
        </IssueFormRow>
      )}
      {isLowILUCRiskConfirmed && (
        <IssueFormRow control={control} field="certification.lowRiskFollowUnusedLandApproach">
          {props => (<IssueFormYesNo {...props} />)}
        </IssueFormRow>
      )}
      {isLowILUCRiskConfirmed && (
        <CertificationDetails
          shouldValidate={shouldValidate}
          control={control}
          nameField="certification.lowRiskAuditCertificationBody"
          emailField="certification.lowRiskAuditCertificationBodyEmail"
          expiryDateField="certification.lowRiskAuditExpiryDate"
          numberField="certification.lowRiskAuditCertificateNumber"
        />
      )}
      {(wasCertifiedAsProductionResidue === true) && (
        <IssueFormRow control={control} field="certification.isLowDisplacementRiskConfirmed">
          {props => (
            <IssueFormYesNo
              control={props.control}
              field={{
                ...props.field,
                // if feedstock.wasCertifiedAsProductionResidue=false, then this field is forced to be set to "false", and cannot be changed
                // casting to trick TS, otherwise it knows that true !== false
                disabled: (wasCertifiedAsProductionResidue as boolean) === false,
              }}
            />
          )}
        </IssueFormRow>
      )}
      {isLowDisplacementRiskConfirmed && (
        <CertificationDetails
          shouldValidate={shouldValidate}
          control={control}
          nameField="certification.lowDisplacementRiskAuditCertificationBody"
          emailField="certification.lowDisplacementRiskAuditCertificationBodyEmail"
          expiryDateField="certification.lowDisplacementRiskAuditExpiryDate"
          numberField="certification.lowDisplacementRiskAuditCertificateNumber"
        />
      )}
    </>
  );
};

const CertificationDetails = (props: {
  control: Control;
  numberField: keyof typeof issueFields;
  expiryDateField: keyof typeof issueFields;
  nameField: keyof typeof issueFields;
  emailField: keyof typeof issueFields;
  shouldValidate: boolean;
}) => {
  return (
    <>
      <IssueFormRow control={props.control} field={props.numberField}>
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={props.control} field={props.expiryDateField}>
        {payload => (
          <FormDatePicker
            control={payload.control}
            field={{ ...payload.field, componentProps: { disablePast: props.shouldValidate ? true : false, disableFuture: false } }}
            transform={{
              onChange: dayjs => dayjs.format('YYYY-MM-DD'),
              value: value => (value === undefined ? value : dayjs(value)),
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={props.control} field={props.nameField}>
        {props => <FormInput {...props} />}
      </IssueFormRow>
      <IssueFormRow control={props.control} field={props.emailField}>
        {props => <FormInput {...props} />}
      </IssueFormRow>
    </>
  );
};
